import { Disclosure, Transition } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/outline'
import { useEffect, useState } from 'react';
import { VolumeUpIcon, LinkIcon } from '@heroicons/react/outline'
import wtf from 'wtf_wikipedia'

type Meaning = {
  partOfSpeech: string,
  definitions: string[]
}

type Props = {
  word: string
  bgClass: string
}


const partsOfSpeech = new Set(['noun', 'verb', 'adjective', 'adverb', 'determiner',
  'article', 'preposition', 'conjunction', 'proper noun',
  'letter', 'character', 'phrase', 'proverb', 'idiom',
  'symbol', 'syllable', 'numeral', 'initialism', 'interjection',
  'definitions', 'pronoun', 'particle', 'predicative', 'participle',
  'suffix',
])

const sectionHeadings = new Set(['etymology', 'derived terms', 'alternative forms',
  'descendants', 'translations', 'anagrams', 'pronunciation', 'english', 'synonyms',
  'antonyms', 'hypernyms', 'hyponyms', 'meronyms', 'holonyms', 'troponyms',
  'related terms', 'coordinate terms', 'references', 'further reading', 'etymology 1', 'etymology 2', 'see also'])

const formOfTemplates = new Set([
  'abbr of',
  'abbreviation of',
  'acronym of',
  'adj form of',
  'alt form',
  'alternative case form of',
  'alternative form of',
  'alternative plural of',
  'alternative spelling of',
  'archaic spelling of',
  'clip of',
  'clipping of',
  'contraction of',
  'dated form of',
  'deliberate misspelling of',
  'dim of',
  'en-comparative of',
  'en-past of',
  'en-superlative of',
  'en-third-person singular of',
  'en-simple past of',
  'euphemistic form of',
  'eye dialect of',
  'form of',
  'hard mutation of',
  'harmonic variant of',
  'honorific alternative case form of',
  'imperative of',
  'imperfective form of',
  'indefinite plural of',
  'inflection of',
  'informal form of',
  'informal spelling of',
  'initialism of',
  'iterative of',
  'lenition of',
  'medieval spelling of',
  'misconstruction of',
  'misromanization of',
  'misspelling of',
  'mixed mutation of',
  'negative of',
  'nonstandard form of',
  'nonstandard spelling of',
  'noun form of',
  'obsolete form of',
  'obsolete spelling of',
  'participle of',
  'passive of',
  'passive participle of',
  'passive past tense of',
  'past active participle of',
  'past participle form of',
  'past participle of',
  'past passive participle of',
  'past tense of',
  'perfect participle of',
  'perfective form of',
  'plural of',
  'present active participle of',
  'present participle of',
  'present participle of',
  'present tense of',
  'rare form of',
  'rare spelling of',
  'reflexive of',
  'short for',
  'singular of',
  'singulative of',
  'slender form of',
  'soft mutation of',
  'spelling of',
  'standard form of',
  'standard spelling of',
  'superlative attributive of',
  'superlative of',
  'superlative predicative of',
  'superseded spelling of',
  'supine of',
  'syncopic form of',
  'synonym of',
  'uncommon form of',
  'uncommon spelling of',
  'verb form of',
  'verbal noun of',
])

const formOfMapping = new Map([
  ['en-past of', 'simple past tense and past participle of'],
  ['en-superlative of', 'superlative form of'],
  ['en-simple past of', 'simple past tense of'],
  ['en-comparative of', 'comparative form of'],
  ['alt form', 'alternative form of'],
  ['en-third-person singular of', 'third-person singular simple present indicative form of'],
])

export const Definition = ({word, bgClass}: Props) => {

  const [phonetics, setPhonetics] = useState('')
  const [phoneticAudio, setPhoneticsAudio] = useState('')
  const [meanings, setMeanings] = useState<Meaning[]>([])
  const [loading, setLoading] = useState(true)
  const [missingEntry, setMissingEntry] = useState(false)

  const playAudio = () => {
    if(!phoneticAudio) {
      return;
    }
    const audio = new Audio(phoneticAudio);
    audio.play();
  }

  useEffect(() => {
    (async () => {
      const data  = await wtf.fetch(word.toLowerCase(), { lang: 'en', wiki: 'wiktionary'})

      if(!data || Array.isArray(data)) {
        setMissingEntry(true)
        setLoading(false)
        return
      }
      const pronunciation = data.sections('Pronunciation');
      if(pronunciation.length > 0) {
        const pronunciationSection = pronunciation[0]
        const ipa = pronunciationSection.templates('ipa')
        if(Array.isArray(ipa) && ipa.length > 0) {
          const ipaTemplate = ipa[0]
          let phonetics = ipaTemplate.wikitext().match(/\/[^/]*\//)
          if(!phonetics) {
            phonetics = ipaTemplate.wikitext().match(/\[.*\]/)
          }
          setPhonetics(phonetics[0])
        }
      }

     const sections = []
     const origSections = data.sections()
     for(let i = 0; i < origSections.length; i++) {
       const title = origSections[i].title().toLowerCase();
       if(partsOfSpeech.has(title)) {
         sections.push(origSections[i])
       } else if (title !== '' && !sectionHeadings.has(title)) {
         // stop adding sections if we bump into another language
         break;
       }
     }

      const getDefinitions = (data: any[], filterPredicate: (s: any) => boolean) => {
        return data.filter(filterPredicate).map((s: any) => s.text())
      }

      const meanings = sections.map((s: any) => {
        let definitions = []
        if(s.list()) {
          definitions = getDefinitions(s.list().data,
          (s: any) => !(s.data.fmt || s.text().includes('*') || !s.text().match(/\d+/)))
          if(!definitions.length) {
            // relax constraints around including a number if we have no definitions
            definitions = getDefinitions(s.list().data,
              (s: any) => !(s.data.fmt || s.text().includes('*')))
              .map((d: string, i: number) => {
                return `${i + 1} ) ${d}`
              })
          }
        } else if(s.templates()) {
          definitions = s.templates().filter((t: any) => {
            return formOfTemplates.has(t.data.template)
          }).map((t: any) => {
            const normalMatch = t.wikitext().match(/{{([^|]*)\|en\|([^|]*).*}}/)
            if(normalMatch) {
              return normalMatch
            }
            return t.wikitext().match(/{{(en-[^|]*)\|([^|]*).*}}/)
          })
          .filter((t: any) => t)
          .map((m: any, index: number) => {
            return <div key={index}> {index + 1}) <span className="italic">{formOfMapping.get(m[1]) ?? m[1]} </span>
            <a className="font-bold" target="_blank" rel="noreferrer" href={`https://en.wiktionary.org/wiki/${m[2]}`}>{m[2]}</a></div>
          })
        }
        return {
          partOfSpeech: s.title(),
          definitions 
        }}).filter((m) => m.definitions.length > 0);
      setMeanings(meanings);
      setLoading(false);

      const resp = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`)
      const extraWordData = await resp.json()
      if(extraWordData.length) {
        const phonetics = extraWordData[0].phonetics
        const audio = phonetics.filter((p: {audio?: string}) => p.audio)
        if(audio.length) {
          setPhoneticsAudio(audio[0].audio)
        }
      }

    })();
  }, [word])


  return (<>
		<Disclosure>
     {({ open }) => (
       <>
         <Disclosure.Button className={`flex items-center w-full justify-between px-4 py-2 text-left text-sm font-medium dark:text-white ${open ? 'rounded-t-lg' : 'rounded-lg mb-1'} mt-1 ${bgClass}`}>
    				<div className='flex flex-row justify-center items-center'>
    				  <div className='font-bold'>{word.toLowerCase()}</div>
    				</div>
           <ChevronUpIcon
             className={`${
               open ? 'rotate-180 transform' : ''
             } h-5 w-5 dark:text-indigo-300 text-indigo-900`}
           />
         </Disclosure.Button>
			<Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0">
         <Disclosure.Panel className="px-4 pt-4 pb-2 bg-slate-50 dark:bg-[#020617] rounded-b-lg mb-1">
    				<div className='flex flex-row justify-center items-center'>
							{phoneticAudio && <VolumeUpIcon onClick={playAudio} className='h-6 w-6 mr-2 cursor-pointer scale-up' />}
    				  <div className='font-bold'>{word.toLowerCase()}</div>
    					<div className='p-2'>{phonetics.length > 0 && phonetics}</div>
						</div>
    				{meanings.map((m: any, i) =>
    				  <div key={i} className='flex flex-col justify-center items-start mt-2'>
    				    <div className='italic'>{m.partOfSpeech}</div>
    				    {m.definitions.map((d:any, j: number)=>
    				    <div className='text-left ml-2 p-1' key={j}>{d}</div>)}
    				  </div>
    				)}
    				{!loading && missingEntry && <div className='flex flex-row mt-2 p-2 italic text-xs'>No wiktionary definition</div>}
    				{!loading &&!missingEntry && <div className='flex flex-row mt-2 p-2'>
    				  <a className='flex justify-center items-center font-bold text-xs' target='_blank' rel='noreferrer' href={`https://en.wiktionary.org/wiki/${word.toLowerCase()}`}><LinkIcon className='h-4 w-4 mr-2 cursor-pointer dark:stroke-white' />Wiktionary</a>
    				</div>}
         </Disclosure.Panel>
		</Transition>
       </>
     )}
    </Disclosure>
  </>
  )
}
